/**
 * DemoStoreContext — Wave 8 session-scoped mutation overlay.
 *
 * Every mutation that would normally write to Supabase gets
 * routed here when Preview Studio is on. State is React-only and
 * page-lifetime — a refresh wipes it back to the seed, which is
 * the exact reset behaviour we want for a keynote run ("hit F5,
 * clean stage").
 *
 * The store hydrates from `src/data/demoOverlay.ts` on first
 * mount. Each entity exposes `list`, `add`, `update`, `remove`.
 * Read hooks that already returned the raw overlay array should
 * be updated to read the store's `list` — otherwise newly added
 * rows won't show up in the UI.
 *
 * DEMO-4 — this store is now the WHOLE demo dataset, and it has to
 * survive a navigation for the core loop to work: convert a lead →
 * land on `/studio/<new demo project id>` → build a gallery → publish
 * → open the client page → favourite → come back. `DemoStoreProvider`
 * is mounted in App.tsx ABOVE `<BrowserRouter>`, so route changes
 * never unmount it and every entity created mid-demo is still there
 * on the next screen. A hard refresh still wipes it, which is the
 * intended reset.
 *
 * The context deliberately uses `Record<string, any>` for row
 * shapes: the demo objects mirror Supabase row shapes but with
 * additional camelCase aliases for legacy consumers, and pinning
 * a strict type across every service would balloon this file
 * without preventing any real bug. The service hooks that call
 * into the store already own the row-shape contract.
 */

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import {
  demoClients,
  demoProjects,
  demoInquiries,
  demoExpenses,
  demoDeliveries,
  demoDeliveryFiles,
  demoDeliveryVideoComments,
  demoGalleries,
  demoGalleryPhotos,
  demoCalendarEvents,
  demoBookingLinks,
  demoInvoices,
  demoGoals,
  // Wave 8.1a — six new entities so the corresponding project tabs
  // (Contracts, Estimates, Documents, Tasks, Milestones, Shots)
  // aren't rendered empty in Preview Studio.
  demoContracts,
  demoEstimates,
  demoDocuments,
  demoTasks,
  demoMilestones,
  demoShots,
  // Wave 8.2 — Nova surfaces (credits + suggestions + conversation
  // history). Same session-scoped mutation model as the rest of the
  // overlay.
  demoNovaCredits,
  demoNovaCreditTransactions,
  demoNovaSuggestions,
  demoNovaConversations,
  // DEMO-4 — the entities the core demo loop mutates. Gallery picks +
  // comments are what makes "client favourites → studio sees the
  // picks" actually work on stage; project files + deliverable
  // comments replaced the 2024-era orbitData fallbacks.
  demoGalleryPicks,
  demoGalleryComments,
  demoProjectFiles,
  demoDeliverableComments,
} from '@/data/demoOverlay';

type Row = Record<string, any>;

interface EntityAPI<T extends Row = Row> {
  /** Current list — call inside a hook body; NOT reactive because
   *  React-query's own `queryFn` re-runs on invalidation. */
  list: () => T[];
  add: (row: T) => T;
  update: (id: string, patch: Partial<T>) => T | null;
  remove: (id: string) => boolean;
}

interface DemoStoreValue {
  clients: EntityAPI;
  projects: EntityAPI;
  inquiries: EntityAPI;
  expenses: EntityAPI;
  deliveries: EntityAPI;
  deliveryFiles: EntityAPI;
  deliveryVideoComments: EntityAPI;
  galleries: EntityAPI;
  /** photos keyed by galleryId */
  galleryPhotos: {
    list: (galleryId: string) => Row[];
    add: (galleryId: string, row: Row) => Row;
    update: (galleryId: string, id: string, patch: Partial<Row>) => Row | null;
    remove: (galleryId: string, id: string) => boolean;
  };
  /** DEMO-4 — `gallery_picks` rows. Flat list; consumers filter by
   *  gallery_id (and picker_identifier for "my picks"). */
  galleryPicks: EntityAPI;
  /** DEMO-4 — `gallery_comments` rows (per-photo client notes). */
  galleryComments: EntityAPI;
  /** DEMO-4 — `project_files` rows. */
  projectFiles: EntityAPI;
  /** DEMO-4 — `comments` rows hanging off a deliverable. */
  deliverableComments: EntityAPI;
  calendarEvents: EntityAPI;
  bookingLinks: EntityAPI;
  invoices: EntityAPI;
  goals: EntityAPI;
  // Wave 8.1a — project-scoped entities for the tabs that were empty
  // in Preview Studio. Same flat-list API; consumers filter by
  // project_id in their own queryFn.
  contracts: EntityAPI;
  estimates: EntityAPI;
  documents: EntityAPI;
  tasks: EntityAPI;
  milestones: EntityAPI;
  shots: EntityAPI;
  // Wave 8.2 — Nova surfaces.
  /** Current credit balance + transactions feed. `consume` is a
   *  no-op in Preview Studio so the balance never drops on stage. */
  novaCredits: {
    balance: () => typeof demoNovaCredits;
    transactions: () => Row[];
    consume: (amount: number, label: string) => void;
  };
  /** Suggestion cards + dismissal tracking. Dismissals live in the
   *  store only, so a refresh restores every card. */
  novaSuggestions: {
    list: () => Row[];
    dismiss: (id: string) => void;
    clearDismissed: () => void;
  };
  /** Seeded past Nova conversations for the history sheet. Read-only
   *  in the store — the chat page never writes back to it, so a
   *  refresh always shows the pristine seed. */
  novaConversations: {
    list: () => Row[];
  };
  /** Increment on any mutation — hooks can key react-query on this
   *  to force a re-render when the store changes. */
  version: number;
}

const DemoStoreContext = createContext<DemoStoreValue | undefined>(undefined);

/** Deep clone a plain-ish array. Demo rows never contain functions
 *  or class instances, so JSON round-trip is enough and cheap. */
function clone<T>(rows: T): T {
  return JSON.parse(JSON.stringify(rows));
}

export function DemoStoreProvider({ children }: { children: ReactNode }) {
  // Version counter — bumped on every mutation so consumers that key
  // react-query queryKeys on it re-fetch (which then reads the fresh
  // list() from the store).
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // Refs hold the mutable copies. We start each entity as a deep
  // clone of the seed so mutations never leak back into the source
  // module (which would persist across route changes even though
  // we want session-scoped state).
  const clientsRef        = useRef<Row[]>(clone(demoClients as Row[]));
  const projectsRef       = useRef<Row[]>(clone(demoProjects as Row[]));
  const inquiriesRef      = useRef<Row[]>(clone(demoInquiries as Row[]));
  const expensesRef       = useRef<Row[]>(clone(demoExpenses as Row[]));
  const deliveriesRef     = useRef<Row[]>(clone(demoDeliveries as Row[]));
  const deliveryFilesRef  = useRef<Row[]>(clone(demoDeliveryFiles as Row[]));
  const videoCommentsRef  = useRef<Row[]>(clone(demoDeliveryVideoComments as Row[]));
  const galleriesRef      = useRef<Row[]>(clone(demoGalleries as Row[]));
  const galleryPhotosRef  = useRef<Record<string, Row[]>>(clone(demoGalleryPhotos as Record<string, Row[]>));
  // DEMO-4 — proofing + files + deliverable comments.
  const galleryPicksRef       = useRef<Row[]>(clone(demoGalleryPicks as Row[]));
  const galleryCommentsRef    = useRef<Row[]>(clone(demoGalleryComments as Row[]));
  const projectFilesRef       = useRef<Row[]>(clone(demoProjectFiles as Row[]));
  const deliverableCommentsRef = useRef<Row[]>(clone(demoDeliverableComments as Row[]));
  const calendarEventsRef = useRef<Row[]>(clone(demoCalendarEvents as Row[]));
  const bookingLinksRef   = useRef<Row[]>(clone(demoBookingLinks as Row[]));
  const invoicesRef       = useRef<Row[]>(clone(demoInvoices as Row[]));
  const goalsRef          = useRef<Row[]>(clone(demoGoals as Row[]));
  // Wave 8.1a — new entities.
  const contractsRef      = useRef<Row[]>(clone(demoContracts as Row[]));
  const estimatesRef      = useRef<Row[]>(clone(demoEstimates as Row[]));
  const documentsRef      = useRef<Row[]>(clone(demoDocuments as Row[]));
  const tasksRef          = useRef<Row[]>(clone(demoTasks as Row[]));
  const milestonesRef     = useRef<Row[]>(clone(demoMilestones as Row[]));
  const shotsRef          = useRef<Row[]>(clone(demoShots as Row[]));
  // Wave 8.2 — Nova surfaces.
  const novaCreditsRef        = useRef(clone(demoNovaCredits));
  const novaCreditTxnsRef     = useRef<Row[]>(clone(demoNovaCreditTransactions as Row[]));
  const novaSuggestionsRef    = useRef<Row[]>(clone(demoNovaSuggestions as Row[]));
  const novaDismissedRef      = useRef<Set<string>>(new Set());
  const novaConversationsRef  = useRef<Row[]>(clone(demoNovaConversations as Row[]));

  const makeAPI = useCallback(
    (ref: React.MutableRefObject<Row[]>): EntityAPI => ({
      list: () => ref.current,
      add: (row) => {
        const next = { ...row, id: row.id ?? `demo-${crypto.randomUUID()}` };
        // Newest first — matches most Supabase ORDER BY created_at DESC
        // queries in the studio.
        ref.current = [next, ...ref.current];
        bump();
        return next;
      },
      update: (id, patch) => {
        const idx = ref.current.findIndex((r) => r.id === id);
        if (idx === -1) return null;
        const merged = {
          ...ref.current[idx],
          ...patch,
          updated_at: new Date().toISOString(),
        };
        ref.current = [
          ...ref.current.slice(0, idx),
          merged,
          ...ref.current.slice(idx + 1),
        ];
        bump();
        return merged;
      },
      remove: (id) => {
        const before = ref.current.length;
        ref.current = ref.current.filter((r) => r.id !== id);
        const removed = ref.current.length !== before;
        if (removed) bump();
        return removed;
      },
    }),
    [bump],
  );

  const value = useMemo<DemoStoreValue>(
    () => ({
      clients:               makeAPI(clientsRef),
      projects:              makeAPI(projectsRef),
      inquiries:             makeAPI(inquiriesRef),
      expenses:              makeAPI(expensesRef),
      deliveries:            makeAPI(deliveriesRef),
      deliveryFiles:         makeAPI(deliveryFilesRef),
      deliveryVideoComments: makeAPI(videoCommentsRef),
      galleries:             makeAPI(galleriesRef),
      galleryPhotos: {
        list: (galleryId) => galleryPhotosRef.current[galleryId] ?? [],
        add: (galleryId, row) => {
          const next = { ...row, id: row.id ?? `demo-${crypto.randomUUID()}` };
          const existing = galleryPhotosRef.current[galleryId] ?? [];
          galleryPhotosRef.current = {
            ...galleryPhotosRef.current,
            [galleryId]: [next, ...existing],
          };
          bump();
          return next;
        },
        update: (galleryId, id, patch) => {
          const existing = galleryPhotosRef.current[galleryId] ?? [];
          const idx = existing.findIndex((r) => r.id === id);
          if (idx === -1) return null;
          const merged = { ...existing[idx], ...patch, updated_at: new Date().toISOString() };
          galleryPhotosRef.current = {
            ...galleryPhotosRef.current,
            [galleryId]: [
              ...existing.slice(0, idx),
              merged,
              ...existing.slice(idx + 1),
            ],
          };
          bump();
          return merged;
        },
        remove: (galleryId, id) => {
          const existing = galleryPhotosRef.current[galleryId] ?? [];
          const before = existing.length;
          const next = existing.filter((r) => r.id !== id);
          galleryPhotosRef.current = {
            ...galleryPhotosRef.current,
            [galleryId]: next,
          };
          const removed = next.length !== before;
          if (removed) bump();
          return removed;
        },
      },
      // DEMO-4
      galleryPicks:        makeAPI(galleryPicksRef),
      galleryComments:     makeAPI(galleryCommentsRef),
      projectFiles:        makeAPI(projectFilesRef),
      deliverableComments: makeAPI(deliverableCommentsRef),
      calendarEvents: makeAPI(calendarEventsRef),
      bookingLinks:   makeAPI(bookingLinksRef),
      invoices:       makeAPI(invoicesRef),
      goals:          makeAPI(goalsRef),
      // Wave 8.1a
      contracts:      makeAPI(contractsRef),
      estimates:      makeAPI(estimatesRef),
      documents:      makeAPI(documentsRef),
      tasks:          makeAPI(tasksRef),
      milestones:     makeAPI(milestonesRef),
      shots:          makeAPI(shotsRef),
      // Wave 8.2 — Nova surfaces.
      novaCredits: {
        balance: () => novaCreditsRef.current,
        transactions: () => novaCreditTxnsRef.current,
        // Consumption is a deliberate no-op in Preview Studio —
        // bumping `version` anyway keeps parity with the real
        // ledger write path so anything keyed on `demoStore.version`
        // re-renders as if a real entry landed.
        consume: (_amount, _label) => {
          bump();
        },
      },
      novaSuggestions: {
        list: () =>
          novaSuggestionsRef.current.filter(
            (s) => !novaDismissedRef.current.has(s.id as string),
          ),
        dismiss: (id) => {
          if (novaDismissedRef.current.has(id)) return;
          novaDismissedRef.current.add(id);
          bump();
        },
        clearDismissed: () => {
          if (novaDismissedRef.current.size === 0) return;
          novaDismissedRef.current.clear();
          bump();
        },
      },
      novaConversations: {
        list: () => novaConversationsRef.current,
      },
      version,
    }),
    [makeAPI, version, bump],
  );

  return (
    <DemoStoreContext.Provider value={value}>{children}</DemoStoreContext.Provider>
  );
}

export function useDemoStore(): DemoStoreValue {
  const ctx = useContext(DemoStoreContext);
  if (ctx) return ctx;
  // Safe fallback for module-level or test-context reads. Returns
  // no-op APIs that always yield the pristine seed. Keeps callers
  // from having to null-check.
  const noopAPI = <T extends Row>(seed: T[]): EntityAPI<T> => ({
    list: () => seed,
    add: (row) => row,
    update: () => null,
    remove: () => false,
  });
  return {
    clients:               noopAPI(demoClients as Row[]),
    projects:              noopAPI(demoProjects as Row[]),
    inquiries:             noopAPI(demoInquiries as Row[]),
    expenses:              noopAPI(demoExpenses as Row[]),
    deliveries:            noopAPI(demoDeliveries as Row[]),
    deliveryFiles:         noopAPI(demoDeliveryFiles as Row[]),
    deliveryVideoComments: noopAPI(demoDeliveryVideoComments as Row[]),
    galleries:             noopAPI(demoGalleries as Row[]),
    galleryPhotos: {
      list: (id) => (demoGalleryPhotos as Record<string, Row[]>)[id] ?? [],
      add: (_id, row) => row,
      update: () => null,
      remove: () => false,
    },
    galleryPicks:        noopAPI(demoGalleryPicks as Row[]),
    galleryComments:     noopAPI(demoGalleryComments as Row[]),
    projectFiles:        noopAPI(demoProjectFiles as Row[]),
    deliverableComments: noopAPI(demoDeliverableComments as Row[]),
    calendarEvents: noopAPI(demoCalendarEvents as Row[]),
    bookingLinks:   noopAPI(demoBookingLinks as Row[]),
    invoices:       noopAPI(demoInvoices as Row[]),
    goals:          noopAPI(demoGoals as Row[]),
    contracts:      noopAPI(demoContracts as Row[]),
    estimates:      noopAPI(demoEstimates as Row[]),
    documents:      noopAPI(demoDocuments as Row[]),
    tasks:          noopAPI(demoTasks as Row[]),
    milestones:     noopAPI(demoMilestones as Row[]),
    shots:          noopAPI(demoShots as Row[]),
    // Wave 8.2 — Nova surfaces. Fallback path returns the pristine
    // seed for reads and no-ops for writes; matches how the other
    // entities behave outside a provider.
    novaCredits: {
      balance: () => demoNovaCredits,
      transactions: () => demoNovaCreditTransactions as Row[],
      consume: () => {},
    },
    novaSuggestions: {
      list: () => demoNovaSuggestions as Row[],
      dismiss: () => {},
      clearDismissed: () => {},
    },
    novaConversations: {
      list: () => demoNovaConversations as Row[],
    },
    version: 0,
  };
}
